/**
 * Fragrance Finder — vanilla JS custom element.
 *
 * A genuine, simple filter tool: the shopper picks Gender, Type, and (optionally)
 * Scent Family from three button groups. On each change we recompute the single
 * best-matching REAL collection URL on this store and update a live "Go to
 * results" link (and reflect the choice in the results text). No AI, no quiz
 * scoring, no invented data — just a direct mapping to existing collections.
 *
 * Markup contract (rendered by sections/fragrance-finder.liquid):
 *   <fragrance-finder>
 *     <div data-group="gender"><button data-value="womens">…</button>…</div>
 *     <div data-group="type"><button data-value="original">…</button>…</div>
 *     <div data-group="family"><button data-value="floral">…</button>…</div>
 *     <a data-result-link>…</a>
 *     <p data-result-text></p>
 *   </fragrance-finder>
 */
class FragranceFinder extends HTMLElement {
  connectedCallback() {
    this.groups = Array.from(this.querySelectorAll('[data-group]'));
    this.resultLink = this.querySelector('[data-result-link]');
    this.resultText = this.querySelector('[data-result-text]');
    this.resetButton = this.querySelector('[data-reset]');

    this.selection = { gender: null, type: null, family: null };

    this.groups.forEach((group) => {
      group.addEventListener('click', (event) => {
        const button = event.target.closest('[data-value]');
        if (!button || !group.contains(button)) return;
        this.#selectValue(group, button);
      });
    });

    if (this.resetButton) {
      this.resetButton.addEventListener('click', () => this.#reset());
    }

    this.#render();
  }

  #selectValue(group, button) {
    const groupName = group.dataset.group;
    const value = button.dataset.value;

    // Toggle off if the same value is clicked again.
    const isAlreadySelected = this.selection[groupName] === value;
    this.selection[groupName] = isAlreadySelected ? null : value;

    Array.from(group.querySelectorAll('[data-value]')).forEach((el) => {
      const active = !isAlreadySelected && el.dataset.value === value;
      el.setAttribute('aria-pressed', active ? 'true' : 'false');
      el.classList.toggle('is-active', active);
    });

    this.#render();
  }

  #reset() {
    this.selection = { gender: null, type: null, family: null };
    this.groups.forEach((group) => {
      Array.from(group.querySelectorAll('[data-value]')).forEach((el) => {
        el.setAttribute('aria-pressed', 'false');
        el.classList.remove('is-active');
      });
    });
    this.#render();
  }

  /**
   * Maps the current gender / type / family selection to the closest real
   * collection URL on this store. Every URL below points to a collection
   * that already exists in the catalog (see the theme's collection list).
   */
  #resolveUrl() {
    const { gender, type, family } = this.selection;

    // Scent family always narrows first when chosen, since scent-family
    // collections (e.g. /collections/floral-perfumes) are the most specific
    // real collections this store has.
    if (family) {
      return `/collections/${family}-perfumes`;
    }

    // Gender + Type combination: no combined collection exists for e.g.
    // "women's + original", so we pick the closer single match. Type
    // (Original vs Alternative) is the more decision-relevant axis when a
    // shopper has made an explicit sourcing choice, so it takes priority
    // over gender when both are picked but no family is picked.
    if (type === 'original') {
      return '/collections/original-perfumes';
    }
    if (type === 'alternative') {
      return '/collections/alternative-fragrances';
    }

    if (gender === 'womens') {
      return '/collections/womens-perfumes';
    }
    if (gender === 'mens') {
      return '/collections/mens-perfumes';
    }
    if (gender === 'unisex') {
      return '/collections/unisex-perfumes';
    }

    // Nothing picked yet: send shoppers to the full perfume catalog.
    return '/collections/all';
  }

  #render() {
    const url = this.#resolveUrl();
    const { gender, type, family } = this.selection;
    const hasSelection = gender || type || family;

    if (this.resultLink) {
      this.resultLink.setAttribute('href', url);
    }

    if (this.resultText) {
      if (!hasSelection) {
        this.resultText.textContent = 'Make a selection above to see matching fragrances.';
      } else {
        const parts = [];
        if (family) parts.push(`${this.#label('family', family)} scent family`);
        if (!family && type) parts.push(this.#label('type', type));
        if (!family && !type && gender) parts.push(this.#label('gender', gender));
        this.resultText.textContent = `Showing: ${parts.join(', ')}`;
      }
    }
  }

  #label(groupName, value) {
    const group = this.querySelector(`[data-group="${groupName}"]`);
    const button = group && group.querySelector(`[data-value="${value}"]`);
    return button ? button.textContent.trim() : value;
  }
}

if (!customElements.get('fragrance-finder')) {
  customElements.define('fragrance-finder', FragranceFinder);
}
